// @ts-strict-ignore
import { PureComponent } from 'react';
import type { ReactNode } from 'react';
import { Button, styled, buttonClasses, Box } from '@mui/material';
import type { SxProps } from '@mui/material';
import { namedColors } from '@/theme';

const borderRadius = 4;
const important = '!important';

const Root = styled(Box)(({ theme }) => ({
    borderRadius,
    boxShadow: `1px 0px ${namedColors.boxShadowBorder},-1px 0px ${namedColors.boxShadowBorder},0 1px ${namedColors.boxShadowBorder},0 -1px ${namedColors.boxShadowBorder}`,
    backgroundColor: theme.palette.background.paper, // todo - Elevation should brighten
    display: 'inline-flex', // inline-flex + 100% looks visually better on overflow
    minWidth: '100%',
}));
const StyledButton = styled(Button, {
    shouldForwardProp: (propName) => propName !== 'checked',
})<{ checked: boolean }>(({ theme, checked }) => ({
    width: '100%',
    margin: 0,
    color:
        theme.palette.mode === 'dark'
            ? theme.palette.text.secondary
            : namedColors.label,
    fontWeight: 'bold',
    letterSpacing: '1px',
    fontSize: '12px',
    '&:hover': {
        borderRadius: borderRadius + important,
    },
    '&:hover + button': {
        boxShadow: 'none!important',
    },
    '&:not(:first-of-type):not(:hover)': {
        boxShadow: `-1px 0 ${namedColors.boxShadowBorder}, 0 -1px ${namedColors.boxShadowBorder}`,
        borderRadius: 0,
    },
    [`&.${buttonClasses.root}`]: {
        borderRadius,
        width: '100%',
        marginTop: `0px !important`,
    },
    [`&.${buttonClasses.disabled}`]: {
        opacity: 0.5,
    },
    ...(checked && {
        backgroundColor: theme.palette.primary.main + important,
        color: namedColors.white + important,
        borderRadius: borderRadius + 'px' + important,
        '& + button': {
            boxShadow: 'none' + important,
        },
        '& ~ button:hover': {
            borderRadius: borderRadius + important,
        },
    }),
}));

export type SegmentedControllerProps<Value extends string | number> = {
    className?: string;
    sx?: SxProps;
    value?: Value;
    disabled?: boolean;
    onChange(value: Value): void;
    items: ReadonlyArray<{
        value: Value;
        label: ReactNode;
        disabled?: boolean;
    }>;
};

export class SegmentedController<
    Value extends string | number = string | number,
> extends PureComponent<SegmentedControllerProps<Value>> {
    render() {
        const { className, sx, items, value, disabled } = this.props;

        return (
            <Root className={className} sx={sx}>
                {items.map((item, index) => (
                    <StyledButton
                        key={index}
                        disabled={item.disabled || disabled}
                        value={item.value}
                        onClick={this.onButtonClick}
                        checked={item.value === value}
                    >
                        {item.label}
                    </StyledButton>
                ))}
            </Root>
        );
    }

    onButtonClick = (event) => {
        let index = 0;
        let node = event.currentTarget;
        while (node.previousElementSibling) {
            node = node.previousElementSibling;
            index++;
        }
        const value = this.props.items[index].value;
        if (value === this.props.value) {
            return;
        }
        this.props.onChange(value);
    };
}

export default SegmentedController;
